import React from "react";
import { LoadingSpinner } from "../../../pages";

interface ProfitabilityChartProps {
  invoiceAmount: number;
  expenseAmount: number;
  isLoading: boolean;
}

const ProfitabilityChart: React.FC<ProfitabilityChartProps> = ({
  invoiceAmount = 0,
  expenseAmount = 0,
  isLoading,
}) => {
  const formatNumber = (number: number) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(number);
  };

  let expensePercentage = 0;
  let invoicePercentage = 0;

  if (invoiceAmount === 0 && expenseAmount === 0) {
    invoicePercentage = 0;
    expensePercentage = 0;
  } else if (invoiceAmount <= 0) {
    invoicePercentage = 0;
    expensePercentage = 100;
  } else if (expenseAmount >= invoiceAmount) {
    invoicePercentage = 0;
    expensePercentage = 100; // loss
  } else {
    invoicePercentage = ((invoiceAmount - expenseAmount) / invoiceAmount) * 100;
    expensePercentage = (expenseAmount / invoiceAmount) * 100;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 sm:p-5 p-3">
      <h3 className="xl:text-[18px] md:text-[17px] text-[16px] font-semibold text-gray-900 mb-6">
        Profitability
      </h3>

      <div className="flex items-center justify-between mb-4">
        <span className="xl:text-[20px] md:text-[18px] text-[16px] font-semibold text-gray-900">
          {isLoading ? <LoadingSpinner /> : `₦${formatNumber(invoiceAmount)}`}
        </span>
        <span className="xl:text-[20px] md:text-[18px] text-[16px] font-semibold text-gray-900">
          {isLoading ? <LoadingSpinner /> : `₦${formatNumber(expenseAmount)}`}
        </span>
      </div>

      {/* Chart bar - placeholder since no actual data */}
      {invoiceAmount !== 0 || expenseAmount !== 0 ? (
        <div className="flex items-center sm:h-[6px] h-[4px] bg-gray-100 rounded-full mb-4">
          <span
            className="h-full bg-[#12B76A] rounded-l-full"
            style={{ width: `${invoicePercentage}%` }}
          />
          <span className="sm:h-[20px] h-[14px] sm:w-[4px] w-[2px] bg-gray-300 rounded-lg" />
          <span
            className="h-full bg-[#FF7F0A] rounded-r-full"
            style={{ width: `${expensePercentage}%` }}
          />
        </div>
      ) : (
        <div className="flex items-center sm:h-[6px] h-[4px] bg-gray-100 rounded-full mb-4">
          <span
            className="h-full bg-gray-200 rounded-l-full"
            style={{ width: `100%` }}
          />
        </div>
      )}

      <div className="flex items-center gap-6 text-sm">
        <div className="flex items-center sm:gap-2 gap-1">
          <div className="sm:w-3 sm:h-3 w-2 h-2 bg-[#12B76A] rounded-full"></div>
          <span className="xl:text-[15px] md:text-[14px] text-[13px] text-gray-600">
            Invoice
          </span>
        </div>
        <div className="flex items-center sm:gap-2 gap-1">
          <div className="sm:w-3 sm:h-3 w-2 h-2 bg-[#FF7F0A] rounded-full"></div>
          <span className="xl:text-[15px] md:text-[14px] text-[13px] text-gray-600">
            Expenses
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProfitabilityChart;
